import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { z } from "zod";
import { Modal } from "../components/dialog.tsx";
import { Alert, Button, SelectField, TextField } from "../components/ui.tsx";
import { ApiError, applyFieldErrors, post } from "../lib/api.ts";
import { useToast } from "../components/toast.tsx";
import { isoDate, requiredText } from "../../shared/setup.ts";
import { LIMITS } from "../../shared/limits.ts";

const schema = z
  .object({
    institution_id: z.string().min(1, "Selecione a instituição."),
    name: requiredText(LIMITS.name, "Informe o nome da oficina."),
    facilitator_name: requiredText(LIMITS.name, "Informe o facilitador."),
    starts_on: isoDate("Informe a data de início."),
    ends_on: isoDate("Informe a data de término."),
    access_until: isoDate("Informe até quando os participantes terão acesso."),
  })
  .refine((v) => v.ends_on >= v.starts_on, { path: ["ends_on"], message: "O término não pode ser anterior ao início." });

/** T04 workshop creation: plan + creator's instructor membership are created atomically, then T13 opens. */
export function NewPlanDialog({ institutions, defaultFacilitator, onClose }: {
  institutions: { id: string; name: string }[];
  defaultFacilitator: string;
  onClose: () => void;
}) {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [confirmEarly, setConfirmEarly] = useState(false);
  const form = useForm<z.input<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { institution_id: institutions[0]?.id ?? "", name: "", facilitator_name: defaultFacilitator, starts_on: "", ends_on: "", access_until: "" },
  });
  const [ends, access] = useWatch({ control: form.control, name: ["ends_on", "access_until"] });
  const early = Boolean(ends && access && access < ends);

  const submit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      const res = await post<{ data: { id: string } }>("/plans", { ...v, confirm_early_cutoff: confirmEarly });
      await qc.invalidateQueries({ queryKey: ["plans"] });
      toast("success", "Oficina criada. Configure eixos, unidades, grupos e participantes.");
      navigate(`/planos/${res.data.id}/configuracoes/eixos`);
    } catch (e) {
      applyFieldErrors(e, form.setError as never);
      setError(e instanceof ApiError ? e.message : "Não foi possível criar a oficina.");
    }
  });

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Nova oficina" description="Dimensão: Gestão de Pessoas. Você será o instrutor desta oficina." wide
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button onClick={() => void submit()} loading={form.formState.isSubmitting} disabled={early && !confirmEarly}>Criar oficina</Button></>}>
      <form noValidate onSubmit={submit}>
        {error && <Alert tone="danger">{error}</Alert>}
        <SelectField label="Instituição" required error={form.formState.errors.institution_id?.message} {...form.register("institution_id")}>
          {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </SelectField>
        <TextField label="Nome da oficina" required autoFocus error={form.formState.errors.name?.message} {...form.register("name")} />
        <TextField label="Facilitador" required error={form.formState.errors.facilitator_name?.message} {...form.register("facilitator_name")} />
        <div className="grid gap-x-4 sm:grid-cols-3">
          <TextField label="Início" type="date" required error={form.formState.errors.starts_on?.message}
            {...form.register("starts_on")} />
          <TextField label="Término" type="date" required error={form.formState.errors.ends_on?.message}
            {...form.register("ends_on", { onChange: (e) => { if (!form.getValues("access_until")) form.setValue("access_until", e.target.value); } })} />
          <TextField label="Acesso até" type="date" required hint="Às 23:59 (horário de Brasília)." error={form.formState.errors.access_until?.message} {...form.register("access_until")} />
        </div>
        {early && (
          <Alert tone="warn" title="Acesso termina antes do fim da oficina">
            <label className="mt-1 flex items-start gap-2">
              <input type="checkbox" className="mt-1 size-4 accent-primary" checked={confirmEarly} onChange={(e) => setConfirmEarly(e.target.checked)} />
              <span>Confirmo que os participantes perderão o acesso antes do término da oficina.</span>
            </label>
          </Alert>
        )}
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
}
